import * as THREE from 'three';
import type { AxialCoord } from '@/types/hex.types';
import { EventBus, GameEvents } from '@/core/EventBus';
import { HexRenderer } from './HexRenderer';
import { coordsEqual } from './HexCoord';

/**
 * Handles hex picking via raycasting
 */
export class HexSelection {
  private camera: THREE.Camera;
  private hexRenderer: HexRenderer;
  private raycaster: THREE.Raycaster;
  private eventBus: EventBus;
  private hoveredCoord: AxialCoord | null = null;
  private selectedCoord: AxialCoord | null = null;

  constructor(
    camera: THREE.Camera,
    hexRenderer: HexRenderer,
    eventBus: EventBus
  ) {
    this.camera = camera;
    this.hexRenderer = hexRenderer;
    this.eventBus = eventBus;
    this.raycaster = new THREE.Raycaster();

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventBus.on<{ normalizedX: number; normalizedY: number }>(
      'input:mousemove',
      (data) => {
        this.handleMouseMove(data.normalizedX, data.normalizedY);
      }
    );

    this.eventBus.on<{ normalizedX: number; normalizedY: number; button: number }>(
      'input:mousedown',
      (data) => {
        if (data.button === 0) {
          this.handleClick(data.normalizedX, data.normalizedY);
        }
      }
    );

    this.eventBus.on('input:mouseleave', () => {
      this.clearHover();
    });
  }

  /**
   * Perform raycast at normalized screen coordinates
   */
  private raycast(normalizedX: number, normalizedY: number): THREE.Intersection | null {
    const pointer = new THREE.Vector2(normalizedX, normalizedY);
    this.raycaster.setFromCamera(pointer, this.camera);

    const meshes = this.hexRenderer.getHexMeshes();
    const intersects = this.raycaster.intersectObjects(meshes, false);

    return intersects.length > 0 ? intersects[0] : null;
  }

  /**
   * Handle mouse movement for hover effects
   */
  private handleMouseMove(normalizedX: number, normalizedY: number): void {
    const intersection = this.raycast(normalizedX, normalizedY);

    if (intersection) {
      // Use serverCoord for events (original non-mirrored coordinate)
      const serverCoord = intersection.object.userData.serverCoord as AxialCoord;

      if (!this.hoveredCoord || !coordsEqual(this.hoveredCoord, serverCoord)) {
        // Unhighlight previous
        if (this.hoveredCoord) {
          this.hexRenderer.unhighlightHex(this.hoveredCoord);
          this.eventBus.emit(GameEvents.HEX_UNHOVERED, {
            coord: this.hoveredCoord,
          });
        }

        // Highlight new (using server coord for hexRenderer which maps by key)
        this.hoveredCoord = serverCoord;
        this.hexRenderer.highlightHex(serverCoord);
        // Emit server coord for game logic
        this.eventBus.emit(GameEvents.HEX_HOVERED, { coord: serverCoord });
      }
    } else {
      this.clearHover();
    }
  }

  /**
   * Handle click for selection
   */
  private handleClick(normalizedX: number, normalizedY: number): void {
    const intersection = this.raycast(normalizedX, normalizedY);

    if (intersection) {
      // Use serverCoord for events (original non-mirrored coordinate)
      const serverCoord = intersection.object.userData.serverCoord as AxialCoord;
      this.selectedCoord = serverCoord;
      // Emit server coord for game logic
      this.eventBus.emit(GameEvents.HEX_CLICKED, { coord: serverCoord });
    }
  }

  /**
   * Clear hover state
   */
  private clearHover(): void {
    if (this.hoveredCoord) {
      this.hexRenderer.unhighlightHex(this.hoveredCoord);
      this.eventBus.emit(GameEvents.HEX_UNHOVERED, { coord: this.hoveredCoord });
      this.hoveredCoord = null;
    }
  }

  /**
   * Get currently hovered coordinate
   */
  getHoveredCoord(): AxialCoord | null {
    return this.hoveredCoord;
  }

  /**
   * Get currently selected coordinate
   */
  getSelectedCoord(): AxialCoord | null {
    return this.selectedCoord;
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.selectedCoord = null;
  }

  /**
   * Update camera reference (if camera changes)
   */
  updateCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }
}
